/**
 * <AIBackdrop /> — dezente technologische Hintergrund-Mikroanimation.
 *
 * Optik: AI Operating System / Stripe / Linear.
 * - Statisches feines Grid (sehr dünn)
 * - Knoten (Nodes) an Grid-Kreuzungen, einer pulsiert dezent
 * - Verbindungslinien mit wanderndem Lichtsegment (Datenfluss)
 * - Komplett SVG + CSS keyframes — performant, GPU-accelerated.
 *
 * Respect `prefers-reduced-motion` (alle CSS keyframes pausieren).
 * Auto-skaliert via viewBox; passt sich der Container-Größe an.
 */
import { useEffect, useState } from "react";

/* Fix node positions (% of viewBox) — keep predictable, "infrastructure"-like. */
const NODES = [
    { x: 14, y: 22, size: 2.4, accent: false },
    { x: 32, y: 36, size: 2.0, accent: false },
    { x: 58, y: 18, size: 2.4, accent: true },   // pulsing accent
    { x: 78, y: 30, size: 2.0, accent: false },
    { x: 22, y: 64, size: 2.0, accent: false },
    { x: 50, y: 58, size: 2.6, accent: true },   // pulsing accent
    { x: 74, y: 72, size: 2.0, accent: false },
    { x: 90, y: 54, size: 2.0, accent: false },
];

/* Connections — pairs of node indices. */
const LINKS = [
    [0, 1], [1, 2], [2, 3],
    [4, 5], [5, 6], [6, 7],
    [1, 5], [2, 5], [3, 7],
];

export default function AIBackdrop({
    /** "soft" | "subtle" | "minimal" — overall intensity */
    intensity = "subtle",
    /** Show pulsing nodes (Default true). */
    nodes = true,
    /** Show grid lines (Default true). */
    grid = true,
    /** Show data-flow light segments traveling along connections. */
    flow = true,
    className = "",
}) {
    const [enableMotion, setEnableMotion] = useState(true);

    useEffect(() => {
        // Respect reduced-motion + skip on touch where parallax is also off
        const m = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handle = () => setEnableMotion(!m.matches);
        handle();
        m.addEventListener?.("change", handle);
        return () => m.removeEventListener?.("change", handle);
    }, []);

    const op = INTENSITY[intensity] || INTENSITY.subtle;

    return (
        <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
            style={{ maskImage: "radial-gradient(ellipse 90% 75% at 50% 45%, black, transparent 95%)",
                     WebkitMaskImage: "radial-gradient(ellipse 90% 75% at 50% 45%, black, transparent 95%)" }}
        >
            <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 80"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    {/* Soft emerald data flow gradient */}
                    <linearGradient id="ai-flow" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="rgba(16,185,129,0)" />
                        <stop offset="45%" stopColor="rgba(16,185,129,0.7)" />
                        <stop offset="55%" stopColor="rgba(16,185,129,0.7)" />
                        <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                    </linearGradient>

                    {/* Champagne accent for one flow */}
                    <linearGradient id="ai-flow-2" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="rgba(200,169,107,0)" />
                        <stop offset="50%" stopColor="rgba(200,169,107,0.55)" />
                        <stop offset="100%" stopColor="rgba(200,169,107,0)" />
                    </linearGradient>

                    {/* Pattern: a single grid square — tiled */}
                    <pattern id="ai-grid" width="6" height="6" patternUnits="userSpaceOnUse">
                        <path
                            d="M 6 0 L 0 0 0 6"
                            fill="none"
                            stroke={`rgba(255,255,255,${op.grid})`}
                            strokeWidth="0.06"
                        />
                    </pattern>

                    <radialGradient id="ai-node-glow">
                        <stop offset="0%" stopColor="rgba(16,185,129,0.55)" />
                        <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                    </radialGradient>
                </defs>

                {/* GRID */}
                {grid ? <rect x="0" y="0" width="100" height="80" fill="url(#ai-grid)" /> : null}

                {/* CONNECTION LINES (static, very faint) */}
                {LINKS.map(([a, b], i) => {
                    const A = NODES[a], B = NODES[b];
                    return (
                        <line
                            key={`l-${i}`}
                            x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                            stroke={`rgba(255,255,255,${op.line})`}
                            strokeWidth="0.08"
                            strokeLinecap="round"
                        />
                    );
                })}

                {/* DATA FLOW SEGMENTS (travel along selected lines) */}
                {flow && enableMotion
                    ? FLOW_DEFS.map((f, i) => {
                          const A = NODES[f.from], B = NODES[f.to];
                          const dx = B.x - A.x, dy = B.y - A.y;
                          const len = Math.hypot(dx, dy);
                          const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                          return (
                              <g
                                  key={`f-${i}`}
                                  transform={`translate(${A.x} ${A.y}) rotate(${angle})`}
                                  style={{
                                      animation: `bm-ai-flow ${f.dur}s ${f.delay}s cubic-bezier(0.45,0,0.55,1) infinite`,
                                      transformOrigin: "0 0",
                                  }}
                              >
                                  <rect
                                      x="0"
                                      y="-0.18"
                                      width={Math.max(2.5, len * 0.18)}
                                      height="0.36"
                                      fill={`url(#${f.accent ? "ai-flow-2" : "ai-flow"})`}
                                      style={{ "--flow-len": `${len}px` }}
                                      data-len={len}
                                  />
                              </g>
                          );
                      })
                    : null}

                {/* NODES */}
                {nodes
                    ? NODES.map((n, i) => (
                          <g key={`n-${i}`}>
                              {/* glow halo on accent nodes */}
                              {n.accent ? (
                                  <circle
                                      cx={n.x}
                                      cy={n.y}
                                      r={n.size * 2.2}
                                      fill="url(#ai-node-glow)"
                                      style={
                                          enableMotion
                                              ? {
                                                    animation: `bm-ai-node-pulse 4.4s ease-in-out ${i * 0.6}s infinite`,
                                                    transformBox: "fill-box",
                                                    transformOrigin: "center",
                                                }
                                              : undefined
                                      }
                                  />
                              ) : null}
                              <circle
                                  cx={n.x}
                                  cy={n.y}
                                  r={n.size * 0.32}
                                  fill={n.accent ? "rgba(16,185,129,0.9)" : `rgba(255,255,255,${op.node})`}
                              />
                          </g>
                      ))
                    : null}
            </svg>
        </div>
    );
}

/* Intensity presets — keep effect well below "look at me" threshold. */
const INTENSITY = {
    minimal: { grid: 0.025, line: 0.04, node: 0.18 },
    subtle:  { grid: 0.04,  line: 0.06, node: 0.28 },
    soft:    { grid: 0.06,  line: 0.10, node: 0.4  },
};

/* Which connections carry a data-flow light segment + timing. */
const FLOW_DEFS = [
    { from: 0, to: 2, dur: 7.5, delay: 0,    accent: false },
    { from: 5, to: 7, dur: 9.0, delay: 1.4,  accent: false },
    { from: 1, to: 5, dur: 8.4, delay: 3.0,  accent: true  },
    { from: 4, to: 6, dur: 10.2, delay: 2.2, accent: false },
];
